import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { CalendarDays, CheckSquare, ChevronLeft, ChevronsUpDown, Home, LogOut, MessageCircle, Menu as MenuIcon, Plus, Settings } from 'lucide-react';
import { BrandIcon, wsColor } from '../../ui/primitives.tsx';
import { useConversations, useWorkspaces, useHome } from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { AccountMenu } from './AccountMenu.tsx';
import { CRM_SECTIONS } from '../../pages/Crm.tsx';

export function useScrolled(threshold = 36) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
}

/** iOS-style header: large title in the page, compact translucent bar once scrolled (mobile). */
/** `actions` show next to the title on desktop; `mobileActions` go in the compact bar (the ＋ tab already covers creation). */
export function PageHeader({ title, subtitle, actions, mobileActions, back, leading }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; mobileActions?: ReactNode; back?: string; leading?: ReactNode }) {
  const scrolled = useScrolled();
  const navigate = useNavigate();
  return (
    <>
      <div className="topbar mobile-only" data-scrolled={scrolled}>
        <div className="topbar-left">
          {back ? (
            <button type="button" className="icon-btn" aria-label="Indietro" onClick={() => navigate(back)}>
              <ChevronLeft size={26} />
            </button>
          ) : null}
        </div>
        <div className="topbar-title">{title}</div>
        <div className="topbar-right">
          {mobileActions}
          <AccountMenu />
        </div>
      </div>
      <header className="page-head" style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {leading}
          <h1 className="large-title">{title}</h1>
          {subtitle && <p className="subtitle">{subtitle}</p>}
        </div>
        {actions && <div className="desktop-only" style={{ marginBottom: 18 }}><div style={{ display: 'flex', gap: 8 }}>{actions}</div></div>}
      </header>
    </>
  );
}

export function TabBar() {
  const { base, orgId } = useOrgCtx();
  const { pathname } = useLocation();
  const conversations = useConversations(orgId);
  const unreadChats = conversations.data?.reduce((n, c) => n + (c.unreadCount > 0 ? 1 : 0), 0) ?? 0;
  const is = (p: string) => (p === base ? pathname === base : pathname.startsWith(p));

  // Inside the CRM "app" (its own full-screen section, not a workspace tab), the bottom bar becomes
  // the CRM's own navigation instead of the usual 5 app-wide tabs - useParams() can't see :wsKey here
  // since TabBar isn't nested under that route, so it's parsed straight out of the current path.
  const crmMatch = pathname.match(/\/w\/([^/]+)\/crm(?:\/([^/]+))?\/?$/);
  if (crmMatch) {
    const [, wsKey, section = 'pipeline'] = crmMatch;
    const crmBase = `${base}/w/${wsKey}/crm`;
    return (
      <nav className="tabbar" aria-label="Navigazione CRM">
        <Link className="tab" to={`${base}/w/${wsKey}`} aria-label="Esci dal CRM">
          <LogOut size={22} strokeWidth={1.8} />
        </Link>
        {CRM_SECTIONS.map((s) => {
          const to = s.id === 'pipeline' ? crmBase : `${crmBase}/${s.id}`;
          const active = section === s.id;
          return (
            <Link key={s.id} className="tab" to={to} aria-label={s.label} aria-current={active ? 'page' : undefined}>
              <s.icon size={24} strokeWidth={active ? 2.4 : 1.8} />
            </Link>
          );
        })}
      </nav>
    );
  }

  return (
    <nav className="tabbar" aria-label="Navigazione principale">
      <Link className="tab" to={base} aria-label="Home" aria-current={is(base) ? 'page' : undefined}>
        <Home size={24} strokeWidth={is(base) ? 2.4 : 1.8} />
      </Link>
      <Link className="tab" to={`${base}/tasks`} aria-label="Task" aria-current={is(`${base}/tasks`) ? 'page' : undefined}>
        <CheckSquare size={24} strokeWidth={is(`${base}/tasks`) ? 2.4 : 1.8} />
      </Link>
      <Link className="tab" to={`${base}/chat`} aria-label={unreadChats ? `Chat, ${unreadChats} da leggere` : 'Chat'} aria-current={is(`${base}/chat`) ? 'page' : undefined} style={{ position: 'relative' }}>
        <MessageCircle size={24} strokeWidth={is(`${base}/chat`) ? 2.4 : 1.8} />
        {unreadChats > 0 && <span className="badge" style={{ position: 'absolute', top: 2, right: 12 }}>{unreadChats}</span>}
      </Link>
      <Link className="tab" to={`${base}/calendar`} aria-label="Calendario" aria-current={is(`${base}/calendar`) ? 'page' : undefined}>
        <CalendarDays size={24} strokeWidth={is(`${base}/calendar`) ? 2.4 : 1.8} />
      </Link>
      <Link className="tab" to={`${base}/menu`} aria-label="Altro" aria-current={is(`${base}/menu`) || is(`${base}/settings`) || is(`${base}/w/`) ? 'page' : undefined}>
        <MenuIcon size={24} strokeWidth={1.8} />
      </Link>
    </nav>
  );
}

export function Sidebar() {
  const { base, orgId, org, me, detail, openQuickAdd, openCreateWorkspace } = useOrgCtx();
  const workspaces = useWorkspaces(orgId);
  const home = useHome(orgId);
  const conversations = useConversations(orgId);
  const unreadChats = conversations.data?.reduce((n, c) => n + (c.unreadCount > 0 ? 1 : 0), 0) ?? 0;
  const [switching, setSwitching] = useState(false);
  const navigate = useNavigate();
  const mine = (home.data?.counts.today ?? 0) + (home.data?.counts.overdue ?? 0);

  return (
    <aside className="sidebar" aria-label="Barra laterale">
      <div style={{ position: 'relative' }}>
        <button type="button" className="org-switch" onClick={() => setSwitching((v) => !v)} aria-expanded={switching}>
          <span className="brand-mark sm" aria-hidden="true"><BrandIcon /></span>
          <span className="name">{org.name}</span>
          <ChevronsUpDown size={15} color="var(--label-3)" />
        </button>
        {switching && (
          <div className="card" style={{ position: 'absolute', top: 46, left: 0, right: 0, zIndex: 5, boxShadow: 'var(--shadow-2)' }}>
            <div className="list">
              {me.organizations.map((o) => (
                <button key={o.id} type="button" className="list-row" onClick={() => { setSwitching(false); navigate(`/o/${o.slug}`); }}>
                  <span className="grow">{o.name}</span>
                  {o.id === org.id && <span className="dot" />}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <button type="button" className="sidebar-create" onClick={() => openQuickAdd()}>
        <Plus size={18} strokeWidth={2.5} /> Nuova task
      </button>

      <nav className="nav-section" aria-label="Sezioni">
        <NavLink end className="nav-link" to={base}><Home size={18} /> Home</NavLink>
        <NavLink className="nav-link" to={`${base}/tasks`}><CheckSquare size={18} /> Le mie task {mine > 0 && <span className="count">{mine}</span>}</NavLink>
        <NavLink className="nav-link" to={`${base}/chat`}><MessageCircle size={18} /> Chat {unreadChats > 0 && <span className="count">{unreadChats}</span>}</NavLink>
        <NavLink className="nav-link" to={`${base}/calendar`}><CalendarDays size={18} /> Calendario</NavLink>
      </nav>

      <nav className="nav-section" aria-label="Spazi">
        <div className="nav-heading" style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ flex: 1 }}>Spazi</span>
          {detail?.capabilities.createWorkspace && (
            <button type="button" className="icon-btn muted" style={{ width: 22, height: 22 }} aria-label="Nuovo spazio" onClick={openCreateWorkspace}>
              <Plus size={15} />
            </button>
          )}
        </div>
        {workspaces.data?.map((w) => (
          <NavLink key={w.id} className="nav-link" to={`${base}/w/${w.key}`}>
            <span className="ws-dot" style={{ '--ws-color': wsColor(w.color), width: 9, height: 9 } as React.CSSProperties} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.name}</span>
            {w.myOpenTasks > 0 && <span className="count">{w.myOpenTasks}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="sidebar-foot nav-section">
        <NavLink className="nav-link" to={`${base}/settings`}><Settings size={18} /> Impostazioni</NavLink>
        <AccountMenu variant="sidebar" />
      </div>
    </aside>
  );
}
